/**
 * Flattening a loaded scene into one geometry, and asking what it says about colour.
 *
 * Three of the formats arrive as a tree of objects rather than as a mesh: an OBJ may hold
 * several named groups, and glTF and 3MF both hold a scene graph proper, with transforms
 * on the nodes.
 *
 * Only OBJ is *drawn* from the merged result — glTF and 3MF keep the separately shaded
 * parts their loader produced, because a picture on a face is not a colour and flattening
 * would throw it away. For those two this is a measuring instrument: bounds and a triangle
 * count need the whole model in one set of coordinates, and nothing more.
 *
 * Two things here are easy to get wrong and expensive to notice:
 *
 * - **The transforms have to be applied.** A node's geometry is expressed in its own
 *   coordinates and placed by its ancestors' matrices. Merging without baking those in
 *   piles every part at the origin, which for an assembly is a heap rather than a model —
 *   and a bounding box measured from that heap is wrong in a way nothing downstream could
 *   detect. OBJ never exercises this, its objects carrying no transforms, which is why
 *   merging got away without it until glTF and 3MF arrived.
 * - **The attributes have to match.** Parts of one file need not carry the same attributes
 *   as each other — one textured object beside one that is not — and merging geometries
 *   with mismatched attribute sets is the usual way that fails. Everything is reduced to
 *   positions before the merge, which makes it certain to succeed rather than something to
 *   check afterwards. Nothing wanting the others survives this far: flat shading derives
 *   its own normals, and what is drawn from a merge is drawn in a single colour.
 */

import { BufferGeometry, Color, Mesh, MeshStandardMaterial, type Object3D } from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/** What three's loaders give a mesh they were told no colour for. */
const DEFAULT_MATERIAL_COLOUR = new Color(0xffffff);

/** Everything drawable under `root`, as one geometry in the root's coordinates. */
export function mergeParts(root: Object3D): BufferGeometry {
  root.updateMatrixWorld(true);

  const parts: BufferGeometry[] = [];

  root.traverse((child) => {
    if (!isMesh(child)) return;

    // Indexed geometry has to be expanded first, or the indices would refer into the wrong
    // part once several are laid end to end. Cloned otherwise, since what follows modifies
    // it and the loader's geometry is not ours to change.
    const flat = child.geometry.index
      ? child.geometry.toNonIndexed()
      : child.geometry.clone();

    for (const name of Object.keys(flat.attributes)) {
      if (name !== 'position') flat.deleteAttribute(name);
    }

    // After the attributes are stripped, so nothing is spent transforming normals that are
    // about to be thrown away.
    flat.applyMatrix4(child.matrixWorld);
    parts.push(flat);
  });

  // A single part needs no merge, and skipping one avoids a copy of the whole geometry for
  // the common case of a file holding one object.
  const only = parts.length === 1 ? parts[0] : undefined;
  if (only) return only;

  return parts.length === 0 ? new BufferGeometry() : mergeGeometries(parts);
}

/**
 * Whether anything under `root` says what colour it should be.
 *
 * Three ways a part can say so, and a reader that knows only the first sees most real
 * files as white: a colour on its material, a base colour texture — which is how nearly
 * every downloaded glTF does it, the Khronos samples included, their materials carrying no
 * colour at all — or vertex colours already on the geometry, which is what a 3MF colour
 * group becomes.
 *
 * A file whose every part is the loader's plain white with no texture is saying nothing,
 * and treating that as a choice would withhold the colour picker from a model that has no
 * colour to show for it. So a plain untextured export stays colourable, and only a file
 * that actually made a choice takes ownership of it.
 */
export function hasOwnColours(root: Object3D): boolean {
  let found = false;

  root.traverse((child) => {
    if (found || !isMesh(child)) return;
    if (child.geometry.hasAttribute('color')) {
      found = true;
      return;
    }

    const materials: readonly MeshStandardMaterial[] = Array.isArray(child.material)
      ? child.material
      : [child.material];
    if (
      materials.some(
        (material) =>
          material.map !== null || !colourOf(material).equals(DEFAULT_MATERIAL_COLOUR),
      )
    ) {
      found = true;
    }
  });

  return found;
}

/** A material need not have a colour at all — a depth or shadow material has none. */
function colourOf(material: MeshStandardMaterial): Color {
  const colour: unknown = material.color;
  return colour instanceof Color ? colour : DEFAULT_MATERIAL_COLOUR;
}

/**
 * Whether this is a mesh, said in a way that keeps its geometry's type.
 *
 * `instanceof Mesh` on its own narrows to a mesh whose type arguments are unknown, which
 * loses the geometry along with them.
 */
function isMesh(child: Object3D): child is Mesh<BufferGeometry, MeshStandardMaterial> {
  return child instanceof Mesh;
}
